// frontend-crm/src/pages/SuperAdmin/TenantsList.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box, Typography, Button, Grid, Card, CardContent, CardActions,
  Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Menu, MenuItem, Tooltip, Alert,
  Skeleton, Divider, InputAdornment, LinearProgress,
  Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Paper, FormControlLabel, Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  Business as BusinessIcon,
  WhatsApp as WhatsAppIcon,
  People as PeopleIcon,
  LocalShipping as PedidosIcon,
  CheckCircle as ActiveIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  QrCode as QrIcon,
  AttachMoney as MoneyIcon,
  Visibility as ViewIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || '';

const authHeader = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

// ─── Color helpers ──────────────────────────────────────────────────────────
const BOT_STATUS_COLOR = {
  connected: '#00D9A5',
  'qr-ready': '#FFB347',
  offline: '#FF6B6B',
  disconnected: '#FF6B6B',
};
const ESTADO_COLOR = {
  activo: '#00D9A5',
  suspendido: '#FF6B6B',
  inactivo: 'rgba(255,255,255,0.3)',
};

// ─── Tarifa Editor ──────────────────────────────────────────────────────────
function TarifasEditor({ tenantId, tarifas, onClose }) {
  const queryClient = useQueryClient();
  const [localTarifas, setLocalTarifas] = useState(tarifas.map(t => ({ ...t })));

  const updateTarifas = useMutation(
    (data) => fetch(`${API_URL}/api/superadmin/tenants/${tenantId}/tarifas`, {
      method: 'PUT', headers: authHeader(), body: JSON.stringify({ tarifas: data })
    }).then(r => r.json()),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('superadmin-tenants');
        onClose();
      }
    }
  );

  const handleChange = (id, field, value) => {
    setLocalTarifas(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  const TIPO_LABEL = { mensajeria: '📦 Mensajería', domicilio: '🏠 Domicilio', paqueteria: '📫 Paquetería' };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 3 }}>
        Configura los precios base y valores por kilómetro para esta empresa.
      </Typography>
      {localTarifas.map((tarifa) => (
        <Box key={tarifa.id} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#fff', mb: 1.5 }}>
            {TIPO_LABEL[tarifa.tipo_servicio] || tarifa.tipo_servicio}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                fullWidth size="small"
                label="Precio Base ($)"
                type="number"
                value={tarifa.precio_base}
                onChange={e => handleChange(tarifa.id, 'precio_base', e.target.value)}
                sx={darkInputSx}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth size="small"
                label="Precio x Km ($)"
                type="number"
                value={tarifa.precio_km}
                onChange={e => handleChange(tarifa.id, 'precio_km', e.target.value)}
                sx={darkInputSx}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth size="small"
                label="Km incluidos"
                type="number"
                value={tarifa.distancia_base_km}
                onChange={e => handleChange(tarifa.id, 'distancia_base_km', e.target.value)}
                sx={darkInputSx}
              />
            </Grid>
          </Grid>
          {tarifa !== localTarifas[localTarifas.length - 1] && (
            <Divider sx={{ mt: 3, borderColor: 'rgba(255,255,255,0.06)' }} />
          )}
        </Box>
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={() => updateTarifas.mutate(localTarifas)}
          disabled={updateTarifas.isLoading}
          sx={{ bgcolor: '#6C63FF', '&:hover': { bgcolor: '#5B54E0' } }}
        >
          Guardar Tarifas
        </Button>
      </Box>
    </Box>
  );
}

// ─── Dark Input helper ──────────────────────────────────────────────────────
const darkInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.04)',
    color: '#fff',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(108,99,255,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#6C63FF' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#6C63FF' },
};

// ─── Tenant Card ─────────────────────────────────────────────────────────────
function TenantCard({ tenant, onEdit, onToggleStatus, onEditTarifas, onRestartBot }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const botColor = BOT_STATUS_COLOR[tenant.bot_status] || '#FF6B6B';
  const estadoColor = ESTADO_COLOR[tenant.estado] || '#fff';

  return (
    <Card
      sx={{
        bgcolor: '#0D1117',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 3,
        overflow: 'visible',
        transition: 'all 0.2s',
        position: 'relative',
        '&:hover': {
          border: '1px solid rgba(108, 99, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(108,99,255,0.15)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      {/* Status indicator line */}
      <Box
        sx={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          bgcolor: estadoColor,
          borderRadius: '3px 3px 0 0',
          opacity: 0.7,
        }}
      />

      <CardContent sx={{ p: 3, pt: 3.5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: tenant.color_primario || '#6C63FF',
              width: 48, height: 48,
              fontSize: 20, fontWeight: 700,
              flexShrink: 0,
              boxShadow: `0 4px 16px ${tenant.color_primario || '#6C63FF'}44`,
            }}
          >
            {tenant.nombre?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
              {tenant.nombre}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              {tenant.dominio || 'Sin dominio configurado'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={e => setMenuAnchor(e.currentTarget)}
            sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#fff' } }}
          >
            <MoreIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Status chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
          <Chip
            size="small"
            label={tenant.estado}
            icon={tenant.estado === 'activo' ? <ActiveIcon style={{ fontSize: 12 }} /> : <BlockIcon style={{ fontSize: 12 }} />}
            sx={{
              height: 22, fontSize: '0.7rem', fontWeight: 600,
              bgcolor: `${estadoColor}22`,
              color: estadoColor,
              border: `1px solid ${estadoColor}44`,
              '& .MuiChip-icon': { color: estadoColor },
            }}
          />
          <Chip
            size="small"
            icon={<WhatsAppIcon style={{ fontSize: 12 }} />}
            label={tenant.bot_status || 'offline'}
            sx={{
              height: 22, fontSize: '0.7rem', fontWeight: 600,
              bgcolor: `${botColor}22`,
              color: botColor,
              border: `1px solid ${botColor}44`,
              '& .MuiChip-icon': { color: botColor },
            }}
          />
        </Box>

        {/* Stats */}
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block' }}>Pedidos hoy</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1 }}>
                {tenant.pedidos_hoy ?? 0}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block' }}>Usuarios</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1 }}>
                {tenant.usuarios_count ?? 0}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Colors */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>Colores:</Typography>
          <Tooltip title={`Primario: ${tenant.color_primario}`}>
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: tenant.color_primario, border: '2px solid rgba(255,255,255,0.1)' }} />
          </Tooltip>
          <Tooltip title={`Secundario: ${tenant.color_secundario}`}>
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: tenant.color_secundario, border: '2px solid rgba(255,255,255,0.1)' }} />
          </Tooltip>
        </Box>
      </CardContent>

      <CardActions sx={{ px: 3, pb: 2, pt: 0, gap: 1 }}>
        <Button
          size="small" variant="outlined" startIcon={<MoneyIcon />}
          onClick={() => onEditTarifas(tenant)}
          sx={{ borderColor: 'rgba(255,255,255,0.1)', color: '#FFB347', '&:hover': { borderColor: '#FFB347', bgcolor: 'rgba(255,179,71,0.1)' }, fontSize: '0.75rem', flex: 1 }}
        >
          Tarifas
        </Button>
        <Button
          size="small" variant="outlined"
          startIcon={tenant.estado === 'activo' ? <BlockIcon /> : <ActiveIcon />}
          onClick={() => onToggleStatus(tenant)}
          sx={{
            borderColor: 'rgba(255,255,255,0.1)',
            color: tenant.estado === 'activo' ? '#FF6B6B' : '#00D9A5',
            '&:hover': { borderColor: tenant.estado === 'activo' ? '#FF6B6B' : '#00D9A5', bgcolor: tenant.estado === 'activo' ? 'rgba(255,107,107,0.1)' : 'rgba(0,217,165,0.1)' },
            fontSize: '0.75rem', flex: 1
          }}
        >
          {tenant.estado === 'activo' ? 'Suspender' : 'Activar'}
        </Button>
      </CardActions>

      {/* Floating Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: '#1A1F2E',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2,
            minWidth: 180,
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { onEdit(tenant); setMenuAnchor(null); }} sx={{ color: 'rgba(255,255,255,0.7)', gap: 1.5 }}>
          <EditIcon fontSize="small" /> Editar Empresa
        </MenuItem>
        <MenuItem onClick={() => { onRestartBot(tenant); setMenuAnchor(null); }} sx={{ color: 'rgba(255,255,255,0.7)', gap: 1.5 }}>
          <RefreshIcon fontSize="small" /> Reiniciar Bot
        </MenuItem>
        <MenuItem onClick={() => { onEditTarifas(tenant); setMenuAnchor(null); }} sx={{ color: 'rgba(255,255,255,0.7)', gap: 1.5 }}>
          <MoneyIcon fontSize="small" /> Editar Tarifas
        </MenuItem>
      </Menu>
    </Card>
  );
}

// ─── Create/Edit Dialog ──────────────────────────────────────────────────────
function TenantFormDialog({ open, onClose, tenant, queryClient }) {
  const isEdit = Boolean(tenant);
  const [form, setForm] = useState({
    nombre: tenant?.nombre || '',
    dominio: tenant?.dominio || '',
    logo_url: tenant?.logo_url || '',
    color_primario: tenant?.color_primario || '#6C63FF',
    color_secundario: tenant?.color_secundario || '#FF6B6B',
    mensajero_logo_url: tenant?.mensajero_logo_url || '',
    mensajero_color_primario: tenant?.mensajero_color_primario || '#2E7D32',
    mensajero_color_secundario: tenant?.mensajero_color_secundario || '#8BC34A',
    app_background_color: tenant?.app_background_color || '#ffffff',
    app_background_image_url: tenant?.app_background_image_url || '',
    app_screens_config: tenant?.app_screens_config || { 
      showHistory: true, 
      showWallet: true, 
      showProfile: true,
      showDeliveryCliente: true,
      showDeliveryComercio: true,
      showDeliveryMensajero: true
    },
    admin_email: '',
    admin_password: '',
  });

  React.useEffect(() => {
    if (tenant) {
      setForm(f => ({
        nombre: tenant.nombre || '',
        dominio: tenant.dominio || '',
        logo_url: tenant.logo_url || '',
        color_primario: tenant.color_primario || '#6C63FF',
        color_secundario: tenant.color_secundario || '#FF6B6B',
        mensajero_logo_url: tenant.mensajero_logo_url || '',
        mensajero_color_primario: tenant.mensajero_color_primario || '#2E7D32',
        mensajero_color_secundario: tenant.mensajero_color_secundario || '#8BC34A',
        app_background_color: tenant.app_background_color || '#ffffff',
        app_background_image_url: tenant.app_background_image_url || '',
        app_screens_config: tenant.app_screens_config || { 
          showHistory: true, 
          showWallet: true, 
          showProfile: true,
          showDeliveryCliente: true,
          showDeliveryComercio: true,
          showDeliveryMensajero: true
        },
      }));
    }
  }, [tenant]);

  const mutation = useMutation(
    (data) => {
      const url = isEdit
        ? `${API_URL}/api/superadmin/tenants/${tenant.id}`
        : `${API_URL}/api/superadmin/tenants`;
      return fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeader(),
        body: JSON.stringify(data)
      }).then(r => r.json());
    },
    {
      onSuccess: (res) => {
        if (res.success) {
          queryClient.invalidateQueries('superadmin-tenants');
          queryClient.invalidateQueries('superadmin-stats');
          onClose();
        }
      }
    }
  );

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleToggleScreen = (e) => setForm(f => ({
    ...f, 
    app_screens_config: { ...f.app_screens_config, [e.target.name]: e.target.checked }
  }));

  const [uploading, setUploading] = useState(false);
  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setForm(f => ({ ...f, [fieldName]: data.data.url }));
      } else {
        alert(data.error || 'Error subiendo la imagen');
      }
    } catch (err) {
      alert('Error de red subiendo la imagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0D1117',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ color: '#fff', fontWeight: 700, pb: 1 }}>
        {isEdit ? '✏️ Editar Empresa' : '🏢 Nueva Empresa'}
      </DialogTitle>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      <DialogContent sx={{ p: 3 }}>
        {mutation.data && !mutation.data.success && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(255,107,107,0.1)', color: '#FF6B6B' }}>
            {mutation.data.error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Nombre de la Empresa *"
              name="nombre" value={form.nombre}
              onChange={handleChange} sx={darkInputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Sub-dominio (ej: empresa.sudominio.com)"
              name="dominio" value={form.dominio}
              onChange={handleChange} sx={darkInputSx}
            />
          </Grid>

          {/* Branding Cliente */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ color: '#6C63FF', mt: 2, mb: 1, fontWeight: 700 }}>
              💎 Branding App Cliente
            </Typography>
            <Divider sx={{ borderColor: 'rgba(108,99,255,0.2)', mb: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="URL Logo Cliente"
              name="logo_url" value={form.logo_url}
              onChange={handleChange} sx={darkInputSx}
              placeholder="https://servidor.com/logo-cliente.png"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton component="label" disabled={uploading} sx={{ color: '#6C63FF' }}>
                      <CloudUploadIcon />
                      <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, 'logo_url')} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth type="color" label="Color Primario Cliente"
              name="color_primario" value={form.color_primario}
              onChange={handleChange} sx={darkInputSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth type="color" label="Color Secundario Cliente"
              name="color_secundario" value={form.color_secundario}
              onChange={handleChange} sx={darkInputSx}
            />
          </Grid>

          {/* Branding Mensajero */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ color: '#00D9A5', mt: 3, mb: 1, fontWeight: 700 }}>
              🚴 Branding App Mensajeros
            </Typography>
            <Divider sx={{ borderColor: 'rgba(0,217,165,0.2)', mb: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="URL Logo Mensajero"
              name="mensajero_logo_url" value={form.mensajero_logo_url}
              onChange={handleChange} sx={darkInputSx}
              placeholder="https://servidor.com/logo-mensajero.png"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton component="label" disabled={uploading} sx={{ color: '#00D9A5' }}>
                      <CloudUploadIcon />
                      <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, 'mensajero_logo_url')} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth type="color" label="Color Primario Mensajero"
              name="mensajero_color_primario" value={form.mensajero_color_primario}
              onChange={handleChange} sx={darkInputSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth type="color" label="Color Secundario Mensajero"
              name="mensajero_color_secundario" value={form.mensajero_color_secundario}
              onChange={handleChange} sx={darkInputSx}
            />
          </Grid>

          {/* Configuración de la Aplicación de Flutter Móvil */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ color: '#FFB347', mt: 3, mb: 1, fontWeight: 700 }}>
              📱 Interfaz App Movil (Fondo y Pantallas)
            </Typography>
            <Divider sx={{ borderColor: 'rgba(255,179,71,0.2)', mb: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Fondo de la Aplicación (Imagen URL)"
              name="app_background_image_url" value={form.app_background_image_url}
              onChange={handleChange} sx={darkInputSx}
              placeholder="Ej: https://servidor.com/tapiz.png"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton component="label" disabled={uploading} sx={{ color: '#FFB347' }}>
                      <CloudUploadIcon />
                      <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, 'app_background_image_url')} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
             <TextField
              fullWidth type="color" label="Color de Fondo Base App"
              name="app_background_color" value={form.app_background_color}
              onChange={handleChange} sx={{...darkInputSx, mb: 2}}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1, display: 'block' }}>Pantallas Activas en Navegación</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={<Checkbox sx={{color: '#6C63FF', '&.Mui-checked': {color: '#6C63FF'}}} name="showHistory" checked={form.app_screens_config.showHistory} onChange={handleToggleScreen} />}
                label={<Typography sx={{color: '#fff', fontSize: '0.85rem'}}>Historial Viajes</Typography>}
              />
              <FormControlLabel
                control={<Checkbox sx={{color: '#6C63FF', '&.Mui-checked': {color: '#6C63FF'}}} name="showWallet" checked={form.app_screens_config.showWallet} onChange={handleToggleScreen} />}
                label={<Typography sx={{color: '#fff', fontSize: '0.85rem'}}>Billetera / Pagos</Typography>}
              />
              <FormControlLabel
                control={<Checkbox sx={{color: '#6C63FF', '&.Mui-checked': {color: '#6C63FF'}}} name="showProfile" checked={form.app_screens_config.showProfile} onChange={handleToggleScreen} />}
                label={<Typography sx={{color: '#fff', fontSize: '0.85rem'}}>Perfil Usuario</Typography>}
              />
            </Box>
            
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mt: 2, mb: 1, display: 'block' }}>Módulos de Delivery (Tiendas y Mensajería)</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={<Checkbox sx={{color: '#00D9A5', '&.Mui-checked': {color: '#00D9A5'}}} name="showDeliveryCliente" checked={form.app_screens_config.showDeliveryCliente} onChange={handleToggleScreen} />}
                label={<Typography sx={{color: '#fff', fontSize: '0.85rem'}}>Cliente: Restaurantes</Typography>}
              />
              <FormControlLabel
                control={<Checkbox sx={{color: '#00D9A5', '&.Mui-checked': {color: '#00D9A5'}}} name="showDeliveryComercio" checked={form.app_screens_config.showDeliveryComercio} onChange={handleToggleScreen} />}
                label={<Typography sx={{color: '#fff', fontSize: '0.85rem'}}>Comercio: Panel Ventas</Typography>}
              />
              <FormControlLabel
                control={<Checkbox sx={{color: '#00D9A5', '&.Mui-checked': {color: '#00D9A5'}}} name="showDeliveryMensajero" checked={form.app_screens_config.showDeliveryMensajero} onChange={handleToggleScreen} />}
                label={<Typography sx={{color: '#fff', fontSize: '0.85rem'}}>Mensajero: Reparto</Typography>}
              />
            </Box>
          </Grid>

          {!isEdit && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mt: 3 }} />
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.4)', mt: 2, mb: 0 }}>
                  Credenciales del Administrador de la Empresa
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Email del Admin *" type="email"
                  name="admin_email" value={form.admin_email}
                  onChange={handleChange} sx={darkInputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Contraseña temporal *" type="password"
                  name="admin_password" value={form.admin_password}
                  onChange={handleChange} sx={darkInputSx}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.4)' }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate(form)}
          disabled={mutation.isLoading || uploading || !form.nombre || (!isEdit && (!form.admin_email || !form.admin_password))}
          sx={{ bgcolor: '#6C63FF', '&:hover': { bgcolor: '#5B54E0' }, borderRadius: 2 }}
        >
          {mutation.isLoading || uploading ? 'Guardando...' : (isEdit ? 'Guardar Cambios' : '🚀 Crear Empresa')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TenantsList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
  const [createOpen, setCreateOpen] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [tarifasTenant, setTarifasTenant] = useState(null);

  const { data: tenantsRes, isLoading, refetch } = useQuery('superadmin-tenants',
    () => fetch(`${API_URL}/api/superadmin/tenants`, { headers: authHeader() }).then(r => r.json()),
    { refetchInterval: 15000 }
  );

  const toggleStatus = useMutation(
    ({ id, estado }) => fetch(`${API_URL}/api/superadmin/tenants/${id}/estado`, {
      method: 'PUT', headers: authHeader(), body: JSON.stringify({ estado })
    }).then(r => r.json()),
    { onSuccess: () => queryClient.invalidateQueries('superadmin-tenants') }
  );

  const restartBot = useMutation(
    (id) => fetch(`${API_URL}/api/superadmin/tenants/${id}/bot/restart`, {
      method: 'POST', headers: authHeader()
    }).then(r => r.json()),
    { onSuccess: () => queryClient.invalidateQueries('superadmin-tenants') }
  );

  const tenants = (tenantsRes?.data || []).filter(t =>
    !search || t.nombre?.toLowerCase().includes(search.toLowerCase()) || t.dominio?.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggleStatus = (tenant) => {
    const newEstado = tenant.estado === 'activo' ? 'suspendido' : 'activo';
    toggleStatus.mutate({ id: tenant.id, estado: newEstado });
  };

  return (
    <Box sx={{ p: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', mb: 0.5 }}>
            Gestión de Empresas
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            {tenants.length} empresa{tenants.length !== 1 ? 's' : ''} en la plataforma
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Tooltip title="Actualizar"><IconButton onClick={refetch} sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff' } }}><RefreshIcon /></IconButton></Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              bgcolor: '#6C63FF',
              '&:hover': { bgcolor: '#5B54E0' },
              borderRadius: 2, fontWeight: 600,
            }}
          >
            Nueva Empresa
          </Button>
        </Box>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Buscar empresa..."
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 18 }} /></InputAdornment> }}
          sx={{ ...darkInputSx, maxWidth: 320, flex: 1 }}
        />
        <Tabs
          value={viewMode}
          onChange={(_, v) => setViewMode(v)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { color: 'rgba(255,255,255,0.4)', minHeight: 36, fontSize: '0.8rem', px: 2 },
            '& .Mui-selected': { color: '#6C63FF !important' },
            '& .MuiTabs-indicator': { bgcolor: '#6C63FF' },
          }}
        >
          <Tab value="cards" label="Tarjetas" />
          <Tab value="table" label="Tabla" />
        </Tabs>
      </Box>

      {(toggleStatus.isLoading || restartBot.isLoading) && (
        <LinearProgress sx={{ mb: 2, bgcolor: 'rgba(108,99,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#6C63FF' } }} />
      )}

      {/* Views */}
      {isLoading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Skeleton variant="rounded" height={280} sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : tenants.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <BusinessIcon sx={{ fontSize: 60, color: 'rgba(255,255,255,0.1)', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.3)' }}>
            {search ? 'No se encontraron empresas' : 'Aún no hay empresas registradas'}
          </Typography>
          {!search && (
            <Button sx={{ mt: 2, color: '#6C63FF' }} onClick={() => setCreateOpen(true)}>
              Crear la primera empresa
            </Button>
          )}
        </Box>
      ) : viewMode === 'cards' ? (
        <Grid container spacing={3}>
          {tenants.map(tenant => (
            <Grid item xs={12} sm={6} lg={4} key={tenant.id}>
              <TenantCard
                tenant={tenant}
                onEdit={setEditTenant}
                onToggleStatus={handleToggleStatus}
                onEditTarifas={setTarifasTenant}
                onRestartBot={(t) => restartBot.mutate(t.id)}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        /* Table View */
        <TableContainer component={Paper} sx={{ bgcolor: '#0D1117', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                {['#', 'Empresa', 'Dominio', 'Estado', 'Bot WA', 'Pedidos Hoy', 'Usuarios', 'Acciones'].map(col => (
                  <TableCell key={col} sx={{ color: 'rgba(255,255,255,0.4)', borderBottom: '1px solid rgba(255,255,255,0.06)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map(t => (
                <TableRow key={t.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' }, '& td': { borderBottom: '1px solid rgba(255,255,255,0.04)' } }}>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>{t.id}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: t.color_primario, width: 32, height: 32, fontSize: 14, fontWeight: 700 }}>
                        {t.nombre?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>{t.nombre}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>{t.dominio || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={t.estado}
                      sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, bgcolor: `${ESTADO_COLOR[t.estado]}22`, color: ESTADO_COLOR[t.estado], border: `1px solid ${ESTADO_COLOR[t.estado]}44` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={t.bot_status || 'offline'}
                      sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, bgcolor: `${BOT_STATUS_COLOR[t.bot_status] || '#FF6B6B'}22`, color: BOT_STATUS_COLOR[t.bot_status] || '#FF6B6B', border: `1px solid ${BOT_STATUS_COLOR[t.bot_status] || '#FF6B6B'}44` }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t.pedidos_hoy ?? 0}</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)' }}>{t.usuarios_count ?? 0}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => setEditTenant(t)} sx={{ color: '#6C63FF' }}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Tarifas">
                        <IconButton size="small" onClick={() => setTarifasTenant(t)} sx={{ color: '#FFB347' }}><MoneyIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title={t.estado === 'activo' ? 'Suspender' : 'Activar'}>
                        <IconButton size="small" onClick={() => handleToggleStatus(t)} sx={{ color: t.estado === 'activo' ? '#FF6B6B' : '#00D9A5' }}>
                          {t.estado === 'activo' ? <BlockIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reiniciar Bot">
                        <IconButton size="small" onClick={() => restartBot.mutate(t.id)} sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#fff' } }}><RefreshIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Dialog */}
      <TenantFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        tenant={null}
        queryClient={queryClient}
      />

      {/* Edit Dialog */}
      {editTenant && (
        <TenantFormDialog
          open={Boolean(editTenant)}
          onClose={() => setEditTenant(null)}
          tenant={editTenant}
          queryClient={queryClient}
        />
      )}

      {/* Tarifas Dialog */}
      <Dialog
        open={Boolean(tarifasTenant)}
        onClose={() => setTarifasTenant(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#0D1117', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: '#fff', fontWeight: 700 }}>
          💰 Tarifas: {tarifasTenant?.nombre}
        </DialogTitle>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
        <DialogContent sx={{ p: 3 }}>
          {tarifasTenant && (
            <TarifasEditor
              tenantId={tarifasTenant.id}
              tarifas={tarifasTenant.tarifas || []}
              onClose={() => setTarifasTenant(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
